import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { getTreatmentPeriodList } from '@/services/main/consent/treatmentPeriod';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import type { ConsentTreatmentPeriodListParams } from '@/types/consentTreatmentPeriod.types';

/**
 * Paginated list for 加療期間切れリスト.
 * `enabled=false` on initial mount — RA2 behaviour: no query until user searches.
 */
export const useConsentTreatmentPeriodList = (
  filters?: ConsentTreatmentPeriodListParams,
  options?: { enabled?: boolean },
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['consent-treatment-period', 'list', tenant, clinicNumber, serviceType, filters],
    queryFn: () => getTreatmentPeriodList(tenant ?? '', clinicNumber ?? '', serviceType, filters),
    enabled: (options?.enabled ?? false) && !!tenant && !!clinicNumber,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast]);

  return query;
};

export default useConsentTreatmentPeriodList;
